import React, { useState } from "react";
import { ButtonGroup as MuiButtonGroup, Button, useTheme } from "@mui/material";
import { useTabListContextValues_unstable } from "@fluentui/react-components";

export interface ButtonOption {
  id: string;
  label: string;
  value?: any;
}

export interface ButtonGroupProps {
  buttons: ButtonOption[];
  defaultSelected?: string;
  onSelectionChange?: (selectedId: string, selectedButton: ButtonOption) => void;
  variant?: "text" | "outlined" | "contained";
  size?: "small" | "medium" | "large";
  color?: "primary" | "secondary" | "error" | "warning" | "info" | "success";
  orientation?: "horizontal" | "vertical";
  fullWidth?: boolean;
}

export const ButtonGroup: React.FC<ButtonGroupProps> = ({
  buttons,
  defaultSelected,
  onSelectionChange,
  size = "medium",
  orientation = "horizontal",
  fullWidth = false,
}) => {
  const theme = useTheme();
  const [selectedId, setSelectedId] = useState<string | null>(defaultSelected || null);

  const handleButtonClick = (button: ButtonOption) => {
    setSelectedId(button.id);
    onSelectionChange?.(button.id, button);
  };

  return (
    <MuiButtonGroup
      variant="outlined"
      size={size}
      orientation={orientation}
      fullWidth={fullWidth}
      aria-label="button group"
      sx={{
        gap: 1,
        "& .MuiButton-root": {
          borderColor: "#e0e0e0",
          color: "#666",
          backgroundColor: "white",
          textTransform: "none",
          fontWeight: 500,
          borderRadius: 1,
          "&:hover": {
            backgroundColor: "#f5f5f5",
            borderColor: "#e0e0e0",
          },
        },
      }}
    >
      {buttons.map((button) => (
        <Button
          key={button.id}
          onClick={() => handleButtonClick(button)}
          sx={{
            ...(selectedId === button.id
              ? {
                  backgroundColor: `${theme.palette.primary.light} !important`,
                  color: `${theme.palette.primary.main} !important`,
                  borderColor: `${theme.palette.primary.main} !important`,
                  fontWeight: 600,
                  "&:hover": {
                    backgroundColor: `${theme.palette.primary.light} !important`,
                  },
                }
              : {}),
          }}
        >
          {button.label}
        </Button>
      ))}
    </MuiButtonGroup>
  );
};
